import { Check, Download, ExternalLink, Film, PencilLine, Play, Send, Undo2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAccountStore } from "../../../../entities/account";
import { MoreMenu } from "../../components/ui/more-menu";
import { notify } from "../../lib/notify";
import { useUrlState } from "../../lib/urlState";
import { ImageCarousel } from "../../components/ImageCarousel";
import { isPostItem } from "../../lib/domain";
import { useCallback, useEffect, useMemo, useState } from "react";
import { montageApi, type TakeRow } from "../../api";
import { Button } from "../../components/ui/button";
import { FilterChip, Segmented } from "../../components/ui/primitives";
import { cn } from "../../lib/utils";

/**
 * «Ролики B2G»: вышедшие ролики отдела-источника для того, кто разносит их
 * по другим пабликам (Саяна из Редакции).
 *
 * Здесь только вышедшее — ни тем, ни черновиков, ни модерации. Взяла ролик —
 * отметила «Взяла», по желанию с пабликом и ссылкой; руководитель B2G видит
 * это у себя в «Видео». Файл качается с сайта, пока его не вычистили; потом
 * он остаётся в Telegram-канале команды, и экран говорит, где искать.
 */

const PERIODS = [
  { value: "1", label: "Сегодня" },
  { value: "3", label: "3 дня" },
  { value: "7", label: "Неделя" },
  { value: "30", label: "Месяц" },
];

type Filter = "all" | "fresh" | "mine";

function daysAgo(days: number): string {
  const d = new Date(Date.now() + 5 * 3600e3 - (days - 1) * 86400e3);
  return d.toISOString().slice(0, 10);
}

const when = (iso: string) =>
  new Date(iso).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Almaty" });
const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "short", timeZone: "Asia/Almaty" });
const mb = (bytes: number) => `${Math.max(1, Math.round(bytes / 1024 / 1024))} МБ`;

export function Takes() {
  // Период и фильтр — в адресе и запомнены (02.11.2026).
  const [period, setPeriod] = useUrlState("period", "3", { remember: "montage:takes:period" });
  const [filterRaw, setFilter] = useUrlState("show", "all");
  const filter: Filter = filterRaw === "fresh" || filterRaw === "mine" ? filterRaw : "all";
  const [data, setData] = useState<{ sources: { name: string }[]; videos: TakeRow[]; today: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await montageApi.takesFeed(daysAgo(Number(period))));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [period]);

  useEffect(() => {
    setData(null);
    void load();
  }, [load]);

  const videos = data?.videos ?? [];
  const shown = useMemo(() => videos.filter((v) =>
    filter === "all" ? true : filter === "mine" ? v.takes.some((t) => t.mine) : v.takes.length === 0,
  ), [videos, filter]);
  const title = data?.sources.length ? `Ролики ${data.sources.map((s) => s.name).join(", ")}` : "Ролики B2G";

  return (
    <div className="flex h-full min-h-0 flex-col gap-5">
      <header className="flex flex-wrap items-center gap-4">
        <h1 className="sr-only">{title}</h1>
        <Segmented id="takes-period" value={period} onChange={setPeriod} options={PERIODS} />
        <span className="flex-1" />
        <span className="rounded-full bg-done-soft px-3 py-1 text-[13px] font-medium text-done-fg">
          сегодня: {data?.today ?? "…"}
        </span>
      </header>

      <div className="flex flex-wrap gap-2">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} count={videos.length}>Все вышедшие</FilterChip>
        <FilterChip active={filter === "fresh"} onClick={() => setFilter("fresh")} count={videos.filter((v) => v.takes.length === 0).length}>Никто не брал</FilterChip>
        <FilterChip active={filter === "mine"} onClick={() => setFilter("mine")} count={videos.filter((v) => v.takes.some((t) => t.mine)).length}>Взятые мной</FilterChip>
      </div>

      {error && <p className="text-sm text-rework-fg">Не удалось загрузить: {error}</p>}

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto pb-6">
        {data === null && !error ? (
          <p className="text-sm text-muted-foreground">Загружаем…</p>
        ) : data && data.sources.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Здесь берут вышедшие ролики другого отдела в свои паблики. Вашему отделу брать не у кого.
          </p>
        ) : shown.length === 0 ? (
          <div className="grid place-items-center gap-2 rounded-[20px] border border-dashed py-16 text-center text-sm text-muted-foreground">
            <Film className="size-6" />
            {videos.length === 0 ? "За этот период ничего не вышло" : "Под этот фильтр роликов нет"}
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-3">
            {shown.map((v) => <TakeCard key={v.id} video={v} onChange={load} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function TakeCard({ video: v, onChange }: { video: TakeRow; onChange: () => Promise<void> }) {
  const mine = v.takes.find((t) => t.mine);
  const others = v.takes.filter((t) => !t.mine);
  const [form, setForm] = useState(false);
  const [pub, setPub] = useState(mine?.public ?? "");
  const [link, setLink] = useState(mine?.link ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const post = isPostItem({ format: v.format });
  // Паблик — из своих аккаунтов (подсказкой), а не вписывать по памяти.
  const myPublics = useAccountStore((s) => s.accounts)
    .map((a) => (a.username ? `@${a.username}` : a.name))
    .filter(Boolean);
  const listId = `take-publics-${v.id}`;
  // Смотреть прямо на сайте (08.10.2026): плеер или слайды вместо обложки.
  const [play, setPlay] = useState<{ url: string; images: { url: string | null; name: string }[] | null } | null>(null);
  const watch = async () => {
    setBusy(true);
    setError(null);
    try {
      const src = await montageApi.takeSource(v.id);
      setPlay({ url: src.playUrl, images: src.images });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const run = async (fn: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChange();
      setForm(false);
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  // «Не брала» — с «Отменить»: отметка возвращается с тем же пабликом и ссылкой.
  const untake = async () => {
    if (!mine) return;
    const was = { public: mine.public ?? "", link: mine.link ?? "" };
    if (!(await run(() => montageApi.untakeItem(v.id)))) return;
    notify(`«${v.title}» — отметка снята`, {
      icon: "info",
      undo: () => void run(() => montageApi.takeItem(v.id, was)),
    });
  };

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await montageApi.takeDownload(v.id);
      window.location.assign(url);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className={cn("flex flex-col overflow-hidden rounded-[20px] border bg-card", mine && "ring-2 ring-done-fg/40")}>
      <div className="relative aspect-video overflow-hidden bg-muted">
        {play ? (
          play.images?.length
            ? <ImageCarousel images={play.images} className="absolute inset-0 h-full w-full p-2" />
            : <video src={play.url} controls autoPlay playsInline className="absolute inset-0 size-full bg-black object-contain" />
        ) : v.posterUrl
          ? <img src={v.posterUrl} alt="" className="size-full object-cover" />
          : <div className="grid size-full place-items-center text-muted-foreground"><Film className="size-6" /></div>}
        {!play && v.file?.available && (
          <button type="button" onClick={watch} disabled={busy} title="Смотреть здесь"
            className="absolute inset-0 grid place-items-center bg-black/0 transition-colors hover:bg-black/25">
            <span className="grid size-12 place-items-center rounded-full bg-black/55 text-white"><Play className="size-5" /></span>
          </button>
        )}
        {mine && !play && (
          <span className="absolute left-2.5 top-2.5 inline-flex items-center gap-1 rounded-full bg-done-fg px-2.5 py-1 text-xs font-medium text-white">
            <Check className="size-3.5" /> Взяла
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-3.5">
        <div className="flex items-start gap-1.5">
          <h3 className="line-clamp-2 flex-1 text-sm font-semibold" title={v.title}>{v.title}</h3>
          {v.link && (
            <a href={v.link} target="_blank" rel="noreferrer" title="Открыть пост" className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground">
              <ExternalLink className="size-4" />
            </a>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {[v.project, v.group?.name, v.editor].filter(Boolean).join(" · ")}
          <br />
          вышло {shortDate(v.publishedAt)} · {v.topic.title}
        </p>

        {others.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Брали: {others.map((t) => `${t.by}${t.public ? ` → ${t.public}` : ""}`).join(", ")}
          </p>
        )}
        {mine && (
          <p className="text-xs">
            Взяла {when(mine.at)}
            {mine.public && <> → <b>{mine.public}</b></>}
            {mine.link && <> · <a href={mine.link} target="_blank" rel="noreferrer" className="underline">ссылка</a></>}
          </p>
        )}

        <span className="flex-1" />

        {v.file && !v.file.available && (
          <p className="rounded-xl bg-muted px-3 py-2 text-xs text-muted-foreground">
            Файл уже убран с сайта — он в Telegram-канале команды{v.group ? ` «${v.group.name}»` : ""}.
            {v.file.telegramUrl && <> <a href={v.file.telegramUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline"><Send className="size-3" />Открыть сообщение</a></>}
          </p>
        )}

        {form && (
          <div className="flex flex-col gap-2 rounded-xl bg-muted/60 p-2.5">
            <input
              value={pub}
              list={myPublics.length ? listId : undefined}
              onChange={(e) => setPub(e.target.value)}
              placeholder={myPublics.length ? "Паблик — выберите из своих (необязательно)" : "Паблик (необязательно)"}
              className="h-9 rounded-lg border bg-background px-3 text-[13px] outline-none focus:ring-2 focus:ring-ring/30"
            />
            {myPublics.length > 0 && (
              <datalist id={listId}>
                {[...new Set(myPublics)].map((name) => <option key={name} value={name} />)}
              </datalist>
            )}
            <input
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="Ссылка на пост (необязательно)"
              className="h-9 rounded-lg border bg-background px-3 text-[13px] outline-none focus:ring-2 focus:ring-ring/30"
            />
            <div className="flex gap-2">
              <Button size="sm" disabled={busy} onClick={() => run(() => montageApi.takeItem(v.id, { public: pub, link }))}>
                {mine ? "Сохранить" : "Взяла"}
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setForm(false)}>Отмена</Button>
            </div>
          </div>
        )}

        {error && <p className="text-xs text-rework-fg">{error}</p>}

        {!form && (
          // Главное — «Опубликовать»; скачать, отметить вручную и «Не брала» —
          // в «⋯» (02.11.2026): три равные кнопки рядом путали.
          <div className="flex flex-wrap items-center gap-2">
            {v.file?.available ? (
              // Выложить с сайта: форма получит файл сама, отметка «Взяла»
              // со ссылкой поставится после публикации.
              <Button size="sm" disabled={busy} onClick={() => navigate(`/publications/new?format=${post ? "post" : "reel"}&takeItem=${encodeURIComponent(v.id)}`)}>
                <Send /> Опубликовать
              </Button>
            ) : !mine && (
              <Button size="sm" disabled={busy} onClick={() => setForm(true)}><Check /> Взяла</Button>
            )}
            <MoreMenu
              align="start"
              items={[
                v.file?.available && { icon: <Download />, label: "Скачать файл", hint: `${v.file.name} · ${mb(v.file.size)}`, onSelect: () => void download() },
                { icon: <PencilLine />, label: mine ? "Паблик и ссылка" : "Отметить вручную", hint: mine ? undefined : "Выложила сама — паблик и ссылка", onSelect: () => setForm(true) },
                mine && { icon: <Undo2 />, label: "Не брала", destructive: true, onSelect: () => void untake() },
              ]}
            />
          </div>
        )}
      </div>
    </article>
  );
}
